import React from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import { Button } from "../components/ui/button";
import {
  Network, Shield, Cloud, FileSearch, ArrowRight, Wifi, Radar, Activity, Bug,
  ScanLine, Code2, Database, ShieldAlert, ShieldCheck, BarChart3, MessagesSquare, FileText, Boxes,
} from "lucide-react";

/**
 * Cybersecurity Tools Landscape — honest positioning page.
 * Mirrors the canonical "Cybersecurity Tools by Category" reference and shows
 * exactly where ArgusSecure competes, where it integrates, and where it stays out of
 * scope. Aligned with GSGD's "publish only what's real" rule.
 */

const CATEGORIES = [
  {
    id: "appsec",
    title: "Application Security",
    blurb:
      "Static + dynamic + dependency scanning of the apps you ship. This is ArgusSecure's home category — SAST + DAST + SCA in one inbox with evidence on every finding.",
    Icon: Shield,
    accent: "argus",
    tools: [
      { name: "BurpSuite",  Icon: Bug,       blurb: "Interactive web proxy + DAST. Industry standard for manual pen-tests." },
      { name: "OWASP ZAP",  Icon: ScanLine,  blurb: "Open-source DAST. Free, scriptable, community-maintained." },
      { name: "Checkmarx",  Icon: Code2,     blurb: "Enterprise SAST. Deep static analysis across many languages." },
      { name: "Veracode",   Icon: ShieldAlert, blurb: "Cloud SAST + SCA + DAST as separate SKUs and consoles." },
    ],
  },
  {
    id: "cloud",
    title: "Cloud Security",
    blurb:
      "Posture, identity, asset, and misconfiguration scanning across AWS, Azure, GCP. ArgusSecure is GA for AWS S3 today; Azure + GCP scanners are on the public roadmap.",
    Icon: Cloud,
    accent: "argus",
    tools: [
      { name: "Prisma Cloud",        Icon: Cloud,     blurb: "Palo Alto CNAPP — workload, identity, posture, runtime." },
      { name: "AWS Security Hub",    Icon: ShieldCheck, blurb: "Findings aggregator native to AWS accounts." },
      { name: "Microsoft Defender",  Icon: Shield,    blurb: "Cloud-and-endpoint suite from Microsoft." },
      { name: "Lacework",            Icon: BarChart3, blurb: "Behaviour-based cloud + workload anomaly detection." },
    ],
  },
  {
    id: "networking",
    title: "Networking",
    blurb:
      "Packet, flow, intrusion-detection, and monitoring tooling at the network layer. Out of ArgusSecure's scope today — we integrate with these tools rather than replacing them.",
    Icon: Network,
    accent: "neutral",
    tools: [
      { name: "Wireshark",   Icon: Wifi,     blurb: "De-facto packet capture + protocol analyser." },
      { name: "Nmap",        Icon: Radar,    blurb: "Port scanner and host fingerprinter." },
      { name: "Snort",       Icon: Activity, blurb: "Signature-based intrusion detection." },
      { name: "Solarwinds",  Icon: BarChart3, blurb: "Network performance and observability platform." },
    ],
  },
  {
    id: "ir",
    title: "Incident Reporting & Forensics",
    blurb:
      "Case management, threat-intel sharing, and forensic analysis after something has gone wrong. ArgusSecure feeds these tools via webhooks — it isn't a replacement.",
    Icon: FileSearch,
    accent: "neutral",
    tools: [
      { name: "TheHive",    Icon: Boxes,         blurb: "Open-source SIRP — case management for SOC teams." },
      { name: "SANS SIFT",  Icon: Database,      blurb: "Forensics toolkit distro from SANS." },
      { name: "MISP",       Icon: MessagesSquare, blurb: "Threat-intel sharing platform." },
      { name: "XPLico",     Icon: FileText,      blurb: "Network forensic analysis tool — protocol reassembly." },
    ],
  },
];

const ARGUS_POSITIONING = {
  appsec: { label: "ArgusSecure competes here", tone: "gold" },
  cloud:  { label: "ArgusSecure competes here (AWS S3 today)", tone: "gold" },
  networking: { label: "ArgusSecure does not cover this category", tone: "muted" },
  ir: { label: "ArgusSecure integrates with this category", tone: "ink" },
};

function PositioningPill({ id }) {
  const p = ARGUS_POSITIONING[id];
  const cls =
    p.tone === "gold"
      ? "border-gold/40 bg-gold/10 text-gold"
      : p.tone === "ink"
      ? "border-line bg-raised text-ink/80"
      : "border-line/60 bg-transparent text-ink/45";
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider border rounded-full px-2.5 py-1 ${cls}`}>
      {p.label}
    </span>
  );
}

function ToolCell({ tool }) {
  const Icon = tool.Icon;
  return (
    <div className="rounded-lg border border-line bg-paper/40 p-4 flex items-start gap-3" data-testid={`tool-${tool.name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-9 h-9 rounded-md bg-raised border border-line flex items-center justify-center shrink-0">
        <Icon size={16} className="text-ink/70" />
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-sm text-ink">{tool.name}</div>
        <div className="text-xs text-ink/55 mt-1 leading-snug">{tool.blurb}</div>
      </div>
    </div>
  );
}

export default function Landscape() {
  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col" data-testid="landscape-page">
      <MarketingHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28 w-full">
        <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">The Tool Landscape</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
          The cybersecurity stack, <em className="text-gold">honestly mapped.</em>
        </h1>
        <p className="text-ink/70 mt-5 max-w-2xl text-base lg:text-lg leading-relaxed">
          No vendor map pretends to do everything. Here is the canonical cybersecurity tools
          landscape — by category — with a straight answer about where ArgusSecure competes,
          where ArgusSecure integrates, and where ArgusSecure stays out of the way.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Button asChild className="bg-gold text-paper hover:bg-gold2 h-11 font-semibold">
            <Link to="/signup" data-testid="landscape-cta-signup">Start a free scan <ArrowRight size={16} className="ml-2" /></Link>
          </Button>
          <Button asChild variant="outline" className="h-11 border-line bg-transparent text-ink">
            <Link to="/why-argus" data-testid="landscape-cta-why">Why ArgusSecure →</Link>
          </Button>
        </div>
      </section>

      {/* Category sections */}
      <section className="max-w-6xl mx-auto px-6 pb-20 w-full space-y-14">
        {CATEGORIES.map((cat) => {
          const CatIcon = cat.Icon;
          return (
            <div key={cat.id} className="grid lg:grid-cols-[280px_1fr] gap-8" data-testid={`landscape-section-${cat.id}`}>
              <div className="lg:sticky lg:top-24 self-start">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-lg border flex items-center justify-center ${cat.accent === "argus" ? "border-gold/50 bg-gold/10" : "border-line bg-raised"}`}>
                    <CatIcon size={20} className={cat.accent === "argus" ? "text-gold" : "text-ink/70"} />
                  </div>
                  <h2 className="font-display text-2xl lg:text-3xl">{cat.title}</h2>
                </div>
                <div className="mt-3">
                  <PositioningPill id={cat.id} />
                </div>
                <p className="text-sm text-ink/65 mt-4 leading-relaxed">{cat.blurb}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {cat.tools.map((t) => <ToolCell key={t.name} tool={t} />)}
              </div>
            </div>
          );
        })}
      </section>

      {/* Honest footer block */}
      <section className="border-t border-line bg-panel/50">
        <div className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <h3 className="font-display text-2xl lg:text-3xl">ArgusSecure replaces two boxes. It doesn't pretend to replace four.</h3>
            <p className="text-ink/70 mt-3 max-w-2xl text-sm leading-relaxed">
              Application Security (SAST + DAST + SCA) and Cloud asset misconfiguration — that's where ArgusSecure
              competes today. Networking and Incident Reporting tools stay best-of-breed; ArgusSecure feeds them
              via webhook and API so your existing stack keeps working.
            </p>
          </div>
          <div className="flex gap-3">
            <Button asChild className="bg-gold text-paper hover:bg-gold2 h-11 font-semibold">
              <Link to="/signup" data-testid="landscape-cta-signup-bottom">Try ArgusSecure free</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
